import React, { useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { useS3Service } from "../../hooks/useS3Service";
import { useS3Filter } from "../../contexts/S3FilterContext";

/**
 * Props for PartnerFilterComponent
 */
export interface PartnerFilterComponentProps {
  disabled?: boolean;
  loading?: boolean;
  basePath?: string;
}

/**
 * PartnerFilterComponent - Multi-select filter for S3 file partners
 *
 * Displays a list of partners extracted from S3 folder structure.
 * Currently supports selection based on available folders, with future
 * support for explicit partner IDs in filenames.
 */
export const PartnerFilterComponent: React.FC<PartnerFilterComponentProps> = ({
  disabled = false,
  loading = false,
  basePath = "",
}) => {
  const { filters, setSelectedPartners } = useS3Filter();
  const { s3Service, isAuthenticated } = useS3Service();
  const [partners, setPartners] = React.useState<string[]>([]);
  const [loadingPartners, setLoadingPartners] = React.useState(false);

  /**
   * Extract unique partner IDs from S3 folder structure
   * Lists folders from root (or basePath) to identify partner folders
   * Partner folders are typically named folders like "Exports", "Distro", etc.
   */
  React.useEffect(() => {
    const extractPartners = async () => {
      try {
        setLoadingPartners(true);
        if (!s3Service || !isAuthenticated) {
          setPartners([]);
          return;
        }

        // List from basePath (empty string = root) to get partner folders
        const result = await s3Service.listObjects(basePath);

        console.log("[PartnerFilter] Raw S3 items:", result.items.slice(0, 10));

        // Extract unique folder names as potential partner IDs
        const partnerSet = new Set<string>();

        // Filter for folders only
        const folders = result.items.filter((item) => item.type === "folder");

        console.log("[PartnerFilter] Folder count:", folders.length);

        folders.forEach((folder) => {
          // Extract first-level folder name from key
          let folderName = folder.key;

          // Remove basePath prefix if present
          if (basePath && folderName.startsWith(basePath)) {
            folderName = folderName.substring(basePath.length);
          }

          // Remove trailing slashes
          folderName = folderName.replace(/\/+$/, "");

          // Get only the first level folder (before any slashes)
          const firstLevel = folderName.split("/")[0];

          console.log(
            "[PartnerFilter] Processing folder:",
            folder.key,
            "-> firstLevel:",
            firstLevel
          );

          // Include only valid partner folder names:
          // - Not empty
          // - Not year folders (4 digits like 2020, 2025)
          // - Not common utility folders
          // - Not numeric-only (2-digit years like "20", "21")
          if (
            firstLevel &&
            !firstLevel.match(/^\d{2,4}$/) && // Exclude 2-4 digit numbers (years: 20, 21, 2020, 2025, etc.)
            firstLevel !== "reports" &&
            firstLevel !== "documents" &&
            firstLevel !== "images"
          ) {
            console.log("[PartnerFilter] Adding partner:", firstLevel);
            partnerSet.add(firstLevel);
          } else {
            console.log("[PartnerFilter] Excluding:", firstLevel);
          }
        });

        const partnerArray = Array.from(partnerSet).sort();
        console.log("[PartnerFilter] Final partners:", partnerArray);
        setPartners(partnerArray);
      } catch (error) {
        console.warn("Failed to extract partners from S3:", error);
        setPartners([]);
      } finally {
        setLoadingPartners(false);
      }
    };

    extractPartners();
  }, [s3Service, isAuthenticated, basePath]);

  /**
   * Create partner options for display
   */
  const partnerOptions = useMemo(
    () =>
      partners.map((partner) => ({
        label: partner.charAt(0).toUpperCase() + partner.slice(1),
        value: partner,
      })),
    [partners]
  );

  /**
   * Get currently selected partner values
   */
  const selectedValues = useMemo(
    () =>
      partnerOptions.filter((option) =>
        filters.selectedPartners.includes(option.value)
      ),
    [filters.selectedPartners, partnerOptions]
  );

  return (
    <Autocomplete
      multiple
      options={partnerOptions}
      getOptionLabel={(option) => option.label}
      value={selectedValues}
      onChange={(_, newValues) => {
        setSelectedPartners(newValues.map((v) => v.value));
      }}
      disabled={disabled || loadingPartners || loading}
      loading={loadingPartners || loading}
      fullWidth
      noOptionsText="No partners found"
      renderInput={(params) => (
        <TextField
          {...params}
          label="Directory"
          placeholder="Select..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {(loadingPartners || loading) && (
                  <CircularProgress color="inherit" size={20} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default PartnerFilterComponent;
